/**
 * Pixel Encroachment Border
 *
 * Gold pixel blocks framing the viewport edges with 6 depth layers per edge.
 * Each layer has progressively larger, more transparent blocks extending
 * further from the border, creating a floating/fading encroachment effect.
 * Behavior prop controls animation style (breathing, scanning, etc.).
 */

import React from 'react';

export type BorderBehavior = 'static' | 'breathe' | 'scan' | 'heartbeat' | 'tremor' | 'hearth' | 'tidal';

interface PixelBorderProps {
  behavior: BorderBehavior;
}

export const PixelBorder: React.FC<PixelBorderProps> = ({ behavior }) => {
  return (
    <div className={`pixel-border border-${behavior}`}>
      {/* Scanning glow overlays (only rendered for scan behavior, CSS handles visibility) */}
      {behavior === 'scan' && (
        <>
          <div className="border-scan-top" />
          <div className="border-scan-bottom" />
          <div className="border-scan-left" />
          <div className="border-scan-right" />
        </>
      )}

      {/* Top edge: wall + 5 depth layers */}
      <div className="pb-edge-top" />
      <div className="pb-top-b" />
      <div className="pb-top-c" />
      <div className="pb-top-d" />
      <div className="pb-top-e" />
      <div className="pb-top-f" />

      {/* Bottom edge: wall + 5 depth layers */}
      <div className="pb-edge-bottom" />
      <div className="pb-bottom-b" />
      <div className="pb-bottom-c" />
      <div className="pb-bottom-d" />
      <div className="pb-bottom-e" />
      <div className="pb-bottom-f" />

      {/* Left edge: wall + 5 depth layers */}
      <div className="pb-edge-left" />
      <div className="pb-left-b" />
      <div className="pb-left-c" />
      <div className="pb-left-d" />
      <div className="pb-left-e" />
      <div className="pb-left-f" />

      {/* Right edge: wall + 5 depth layers */}
      <div className="pb-edge-right" />
      <div className="pb-right-b" />
      <div className="pb-right-c" />
      <div className="pb-right-d" />
      <div className="pb-right-e" />
      <div className="pb-right-f" />

      {/* Corner clusters */}
      <div className="pb-corner-tl" />
      <div className="pb-corner-tr" />
      <div className="pb-corner-bl" />
      <div className="pb-corner-br" />
    </div>
  );
};
